// GST Control ledger (requirement 13) — a monthly rollup, not per-invoice.
// Output GST and recognized GST-withheld are always DERIVED from
// approved_invoice_snapshots and receipt_allocations respectively (never
// stored redundantly, per the same "don't duplicate what's derivable"
// principle Stage 1 applied to revenue recognition) — only the manually
// entered fields (input credit, deposited amount, filing reference,
// adjustments, status) are persisted here.
import {
  arrayUnion, collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { calculateGstBalance } from "./gstCalculator";
import { activeSnapshotDocs } from "./voidedInvoice";

const PERIODS = "gst_control_periods";
const SNAPSHOTS = "approved_invoice_snapshots";
const ALLOCATIONS = "receipt_allocations";

function chunk(array, size) {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) chunks.push(array.slice(i, i + size));
  return chunks;
}

// Output GST is charged/recognized at invoice time regardless of receipts
// (requirement 13: "do not calculate GST payable from bank receipts alone").
export async function computeOutputGstForMonth(monthKey) {
  const q = query(collection(db, SNAPSHOTS), where("accountingPeriod", "==", monthKey));
  const result = await getDocs(q);
  return activeSnapshotDocs(result).reduce((sum, d) => sum + Number(d.data().totals?.gstAmount || 0), 0);
}

// GST withheld is only counted once a receipt has actually recognized it —
// this is receipt-driven by design (you can't claim a credit for withholding
// that hasn't been confirmed against real cash yet).
export async function computeRecognizedGstWithheldForMonth(monthKey) {
  const snapshotsQuery = query(collection(db, SNAPSHOTS), where("accountingPeriod", "==", monthKey));
  const snapshotsResult = await getDocs(snapshotsQuery);
  const snapshotIds = activeSnapshotDocs(snapshotsResult).map((d) => d.id);
  if (!snapshotIds.length) return 0;

  let total = 0;
  for (const batch of chunk(snapshotIds, 30)) {
    const allocationsQuery = query(collection(db, ALLOCATIONS), where("snapshotId", "in", batch));
    const allocationsResult = await getDocs(allocationsQuery);
    allocationsResult.docs.forEach((d) => {
      const data = d.data();
      if (!data.reversed) total += Number(data.gstWithheldRecognized || 0);
    });
  }
  return Math.round(total);
}

function defaultPeriod(monthKey) {
  return {
    monthKey,
    inputGstCredit: 0,
    gstDeposited: 0,
    filingReference: "",
    paymentDate: null,
    adjustments: [],
    status: "OPEN"
  };
}

export async function getOrCreateGstControlPeriod(monthKey) {
  const periodRef = doc(db, PERIODS, monthKey);
  const existing = await getDoc(periodRef);
  if (existing.exists()) return { id: monthKey, ...existing.data() };
  const created = defaultPeriod(monthKey);
  await setDoc(periodRef, { ...created, createdAt: serverTimestamp() });
  return { id: monthKey, ...created };
}

export async function updateGstControlPeriod(monthKey, updates) {
  const periodRef = doc(db, PERIODS, monthKey);
  await setDoc(periodRef, {
    ...updates,
    updatedBy: auth.currentUser?.uid || null,
    updatedByEmail: auth.currentUser?.email || null,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

export async function addGstAdjustment(monthKey, { type, amount, reason }) {
  if (!["CREDIT", "DEBIT"].includes(type)) throw new Error("Adjustment type must be CREDIT or DEBIT.");
  if (!(Number(amount) > 0)) throw new Error("Enter an adjustment amount greater than zero.");
  if (!String(reason || "").trim()) throw new Error("Enter a reason for the adjustment.");
  await getOrCreateGstControlPeriod(monthKey);
  const periodRef = doc(db, PERIODS, monthKey);
  await setDoc(periodRef, {
    adjustments: arrayUnion({
      type, amount: Math.round(Number(amount)), reason: String(reason).trim(),
      enteredByEmail: auth.currentUser?.email || null, at: new Date().toISOString()
    }),
    updatedBy: auth.currentUser?.uid || null,
    updatedByEmail: auth.currentUser?.email || null,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

// Assembles the full GST Control tab row for one month: derived figures +
// stored manual fields + the resulting balance.
export async function getGstControlSummary(monthKey) {
  const [outputGst, recognizedGstWithheld, period] = await Promise.all([
    computeOutputGstForMonth(monthKey),
    computeRecognizedGstWithheldForMonth(monthKey),
    getOrCreateGstControlPeriod(monthKey)
  ]);
  const balance = calculateGstBalance({
    outputGst,
    recognizedGstWithheld,
    inputGstCredit: period.inputGstCredit,
    gstDeposited: period.gstDeposited,
    adjustments: period.adjustments
  });
  return { monthKey, outputGst, recognizedGstWithheld, ...period, balance };
}
